import api from '@/lib/api/axios'

/**
 * Post-recovery statistics — the client contract.
 *
 * Every number that could be an average arrives with the sample it rests on. `observations`,
 * `tasksMeasured` and `coverage` are not optional extras on this screen: with a handful of
 * measured tasks in the whole estate, an average without its coverage is a decoration.
 */

export interface PostRecoveryRun {
  requestId: number
  objectId: string
  description?: string | null
  scenario: number | null
  runbookFingerprint: string | null
  taskCount: number
  tasksCompleted: number
  tasksIssue: number
  tasksOutstanding: number
  /** Measured AND not an outlier — the sample actualMinutes actually rests on. */
  tasksMeasured: number
  /** Every task with an actual, outliers included. Always >= tasksMeasured. */
  tasksWithActual: number
  tasksOutlier: number
  tasksSkipped: number
  /** The whole runbook's plan. NEVER compare this with actualMinutes. */
  plannedMinutes: number
  /** The plan for exactly the tasks actualMinutes sums. This is the comparator. */
  plannedMinutesOfMeasured: number
  actualMinutes: number | null
  waitingMinutes: number | null
  /** Summed from the per-task variances over the measured, non-outlier tasks. */
  varianceMinutes: number | null
  plannedStart: string | null
  plannedFinish: string | null
  actualStart: string | null
  actualFinish: string | null
  isFinished: boolean
  plannedElapsedMinutes: number | null
  actualElapsedMinutes: number | null
}

export type NotRunReason =
  | 'no_runbook' | 'no_copies' | 'all_copies_red' | 'not_started' | 'not_attempted'

export interface NotRunObject {
  recoverRequestsId: number
  objectId: string | null
  description: string | null
  reason: NotRunReason
  runbookTasks: number
  versionCount: number
  hasCleanChain: boolean
}

export interface SetOverview {
  runs: PostRecoveryRun[]
  notRun: NotRunObject[]
  totals: {
    runCount: number
    runsFinished: number
    runsMeasured: number
    plannedMinutes: number
    actualMinutes: number
    plannedMinutesOfMeasured: number
    varianceMinutes: number
    waitingMinutes: number
    tasksTotal: number
    tasksMeasured: number
    tasksOutlier: number
    /** DISTINCT objects that never ran. notRunRequests counts the request rows. */
    notRunCount: number
    notRunRequests: number
  } | null
}

export interface PostRecoveryTask {
  id: number
  taskNo: number | null
  taskKey: string | null
  task: string | null
  status: string | null
  waitGo: string | null
  plannedMinutes: number | null
  actualMinutes: number | null
  varianceMinutes: number | null
  waitingMinutes: number | null
  startVarianceMinutes: number | null
  durationMeasured: boolean
  isOutlier: boolean
  commentCount: number
  plannedStartMin: number | null
  actualStartMin: number | null
  plannedStart: string | null
  plannedFinish: string | null
  actualStart: string | null
  actualFinish: string | null
  eligibleAt: string | null
}

export interface RunTasks {
  originIso: string | null
  run: PostRecoveryRun | null
  tasks: PostRecoveryTask[]
}

export interface TaskHistoryRow {
  taskKey: string
  taskNo: number | null
  task: string | null
  runsWithTask: number
  runbookRevisions: number
  observations: number
  outlierObservations: number
  plannedMinutes: number | null
  minActualMinutes: number | null
  maxActualMinutes: number | null
  avgActualMinutes: number | null
  stdDevActualMinutes: number | null
  avgWaitingMinutes: number | null
  commentCount: number
  /** comparable = present in every run of this object; partial = inserted or removed along the way */
  presence: 'comparable' | 'partial'
  proposedMinutes: number | null
}

export interface ObjectHistory {
  object: { objectId: string; description: string | null; rtoSeconds: number | null } | null
  runCount: number
  runs: PostRecoveryRun[]
  tasks: TaskHistoryRow[]
  coverage: {
    taskCount: number
    comparable: number
    partial: number
    withEnoughObservations: number
    minObservations: number
    runbookRevisions: number
  }
  proposal: {
    plannedTotalMinutes: number
    proposedTotalMinutes: number
    basedOnTasks: number
  }
}

export type CommentFlag =
  | 'wrong_step' | 'wrong_timing' | 'missing_step' | 'unnecessary_step' | 'note'

export interface TaskComment {
  id: number
  flag: CommentFlag
  comment: string | null
  userId: number | null
  userName: string | null
  createdAt: string
}

export type LessonAccuracy = 'accurate' | 'minor_issues' | 'significant_issues'
export type LessonTiming = 'about_right' | 'too_optimistic' | 'too_pessimistic'

export interface RunbookLesson {
  id: number
  objectId: string | null
  accuracy: LessonAccuracy | null
  timing: LessonTiming | null
  proposedRTOMinutes: number | null
  comment: string | null
  userId: number | null
  createdAt: string
}

export interface ObjectLessons {
  lessons: (RunbookLesson & { requestId: number; userName: string | null })[]
  comments: (TaskComment & { requestId: number; taskNo: number | null; taskKey: string | null })[]
}

const unwrap = <T,>(r: { data: { data: T } }) => r.data.data

export const postRecoveryApi = {
  setOverview: (setId: number) =>
    api.get<{ data: SetOverview }>(`/api/post-recovery/sets/${setId}/overview`).then(unwrap),

  runTasks: (requestId: number) =>
    api.get<{ data: RunTasks }>(`/api/post-recovery/runs/${requestId}/tasks`).then(unwrap),

  objectHistory: (objectId: string) =>
    api.get<{ data: ObjectHistory }>(`/api/post-recovery/objects/${encodeURIComponent(objectId)}/history`).then(unwrap),

  objectLessons: (objectId: string) =>
    api.get<{ data: ObjectLessons }>(`/api/post-recovery/objects/${encodeURIComponent(objectId)}/lessons`).then(unwrap),

  taskComments: (taskId: number) =>
    api.get<{ data: TaskComment[] }>(`/api/post-recovery/tasks/${taskId}/comments`).then(unwrap),

  addTaskComment: (taskId: number, body: { flag: CommentFlag; comment?: string }) =>
    api.post(`/api/post-recovery/tasks/${taskId}/comments`, body),

  getLesson: (requestId: number) =>
    api.get<{ data: RunbookLesson | null }>(`/api/post-recovery/runs/${requestId}/lesson`).then(unwrap),

  saveLesson: (requestId: number, body: {
    accuracy?: LessonAccuracy | null
    timing?: LessonTiming | null
    proposedRTOMinutes?: number | null
    comment?: string | null
  }) => api.post(`/api/post-recovery/runs/${requestId}/lesson`, body),
}
